import React, { useState } from "react";
import { Layout } from "../components/Layout";
import { useLanguage } from "../context/LanguageContext";
import { siteContent } from "../mock/rosablu.mock";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";

export default function ContactPage() {
  const { lang } = useLanguage();
  const contact = siteContent.contact[lang];
  const info = siteContent.contactInfo;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageBox, setMessageBox] = useState(null); // { type, text } | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessageBox(null);

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch(
        "https://rosablu-backend.onrender.com/api/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Request failed");
      }

      setMessageBox({
        type: "success",
        text:
          lang === "it"
            ? "Richiesta inviata correttamente. Ti risponderemo il prima possibile."
            : "Request sent successfully. We will get back to you as soon as possible.",
      });

      e.target.reset();
      setStep(1);
    } catch (error) {
      console.error("Error submitting form:", error);
      setMessageBox({
        type: "error",
        text:
          lang === "it"
            ? "Ops... invio fallito :( Ci scusiamo per il disagio. Riprova più tardi o contattaci via email/WhatsApp."
            : "Oops... sending failed :( We are sorry for the inconvenience. Please try again later or contact us via email/WhatsApp.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#1f2933] py-14 text-slate-50 md:py-18">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          {messageBox && (
            <div className="mb-6 flex justify-center">
              <div
                className={`max-w-xl rounded-2xl px-4 py-3 text-center text-sm font-medium shadow-lg ${
                  messageBox.type === "success"
                    ? "bg-emerald-600/90 text-white"
                    : "bg-red-600/90 text-white"
                }`}
              >
                {messageBox.text}
              </div>
            </div>
          )}

          <div className="mb-8 grid gap-10 md:grid-cols-[1.1fr,0.9fr]">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl">
                {contact.title}
              </h1>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-200 md:text-base">
                {contact.subtitle}
              </p>
              <p className="mt-1 max-w-lg text-xs leading-relaxed text-slate-300 md:text-sm">
                {lang === "it"
                  ? "Siamo disponibili 24 ore su 24 per informazioni e richieste: puoi scriverci tramite form, email o WhatsApp."
                  : "We are available 24/7 for information and requests: feel free to contact us via form, email or WhatsApp."}
              </p>

              {/* STEP INDICATOR */}
              <div className="mt-5 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border text-[0.7rem] ${
                      step === 1
                        ? "border-[#f2c9b0] bg-[#f2c9b0] text-slate-900"
                        : "border-slate-500 bg-transparent text-slate-200"
                    }`}
                  >
                    1
                  </span>
                  <span>
                    {lang === "it" ? "Arrivo / Partenza" : "Arrival / Departure"}
                  </span>
                </div>
                <div className="h-px flex-1 bg-slate-600" />
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border text-[0.7rem] ${
                      step === 2
                        ? "border-[#f2c9b0] bg-[#f2c9b0] text-slate-900"
                        : "border-slate-500 bg-transparent text-slate-200"
                    }`}
                  >
                    2
                  </span>
                  <span>{lang === "it" ? "Dati" : "Details"}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4 text-sm">
                {step === 1 && (
                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                          {lang === "it" ? "Arrivo" : "Arrival"}
                        </label>
                        <Input
                          name="arrival"
                          className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                          placeholder={
                            lang === "it" ? "Es. 10 marzo" : "e.g. 10 March"
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                          {lang === "it" ? "Partenza" : "Departure"}
                        </label>
                        <Input
                          name="departure"
                          className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                          placeholder={
                            lang === "it" ? "Es. 15 marzo" : "e.g. 15 March"
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                        {contact.form.guests}
                      </label>
                      <Input
                        name="guests"
                        className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                        placeholder={
                          lang === "it" ? "Es. 2 adulti" : "e.g. 2 adults"
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      className="mt-2 rounded-full bg-[#f2c9b0] px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-900 hover:bg-[#e8b898]"
                      onClick={() => setStep(2)}
                    >
                      {lang === "it" ? "Avanti" : "Next"}
                    </Button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                          {contact.form.name}
                        </label>
                        <Input
                          name="name"
                          required
                          className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                          placeholder="Laura Rossi"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                          {contact.form.email}
                        </label>
                        <Input
                          type="email"
                          name="email"
                          required
                          className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                          placeholder="nome@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                        {lang === "it" ? "Telefono" : "Phone"}
                      </label>
                      <Input
                        name="phone"
                        className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                        placeholder={lang === "it" ? "+39 ..." : "+.."}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                        {contact.form.message}
                      </label>
                      <Textarea
                        name="message"
                        rows={4}
                        className="border-slate-600 bg-slate-900/60 text-sm text-slate-50 placeholder:text-slate-400 focus-visible:ring-[#f2c9b0]"
                        placeholder={
                          lang === "it"
                            ? "Raccontaci che tipo di soggiorno desideri..."
                            : "Tell us what kind of stay you are dreaming of..."
                        }
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full border-slate-400 bg-transparent px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100 hover:bg-slate-800"
                        onClick={() => setStep(1)}
                      >
                        {lang === "it" ? "Indietro" : "Back"}
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="rounded-full bg-[#f2c9b0] px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-900 hover:bg-[#e8b898] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting
                          ? lang === "it"
                            ? "Invio..."
                            : "Sending..."
                          : contact.form.submit}
                      </Button>
                    </div>
                    <p className="mt-3 text-[0.68rem] text-slate-400">
                      {lang === "it"
                        ? "Ti risponderemo al più presto via email o telefono."
                        : "We will reply as soon as possible via email or phone."}
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Colonna contatti + mappa + come arrivare */}
            <div className="space-y-5 text-sm">
              <Card className="rounded-3xl border-slate-700 bg-slate-900/60 p-5 text-slate-50">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                  {lang === "it" ? "Dati di contatto" : "Contact details"}
                </p>
                <div className="mt-3 space-y-2">
                  <p className="flex items-start gap-2 text-sm">
                    <MapPin className="mt-0.5 h-4 w-4" />
                    <span>{info.address}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${info.phone}`} className="hover:underline">
                      {info.phone}
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${info.email}`} className="hover:underline">
                      {info.email}
                    </a>
                  </p>
                </div>
              </Card>

              <Card className="rounded-3xl border-slate-700 bg-slate-900/60 p-5 text-slate-50">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                  Social
                </p>
                <div className="mt-3 space-y-2 text-sm">
                  <a
                    href="https://instagram.com/rosabluapartment"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Instagram className="h-4 w-4" />
                    <span>{info.socials.instagram}</span>
                  </a>
                  <a
                    href="https://facebook.com/rosabluapartment"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:underline"
                  >
                    <Facebook className="h-4 w-4" />
                    <span>{info.socials.facebook}</span>
                  </a>
                </div>
              </Card>

              <Card className="overflow-hidden rounded-3xl border-slate-700 bg-slate-900/60 p-0 text-slate-50">
                <div className="h-56 w-full">
                  <iframe
                    title="Mappa Rosa Blu Apartment"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2738.255646463959!2d10.827788315625546!3d46.34739207912126!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47827f6e4a3f1db1%3A0x0000000000000000!2sVia%20del%20Zicol%2C%204%2C%2038020%20Commezzadura%20TN!5e0!3m2!1sit!2sit!4v1700000000001"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Card>

              <Card className="rounded-3xl border-slate-700 bg-slate-900/60 p-5 text-slate-50">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                  {lang === "it" ? "Come arrivare" : "How to reach us"}
                </p>
                <div className="mt-3 space-y-2 text-xs leading-relaxed md:text-sm">
                  {lang === "it" ? (
                    <>
                      <p>
                        Rosa Blu Apartment si trova in Via del Zicol 4, 38020
                        Commezzadura (TN), nel cuore della Val di Sole.
                      </p>
                      <p>
                        <span className="font-semibold">In auto:</span>{" "}
                        dall&apos;A22 del Brennero esci a Trento Nord o S.
                        Michele all&apos;Adige e segui le indicazioni per Val di
                        Non / Val di Sole (SS43 e poi SS42) fino a
                        Commezzadura. Una volta in paese, segui Via del Zicol
                        fino al civico n4.
                      </p>
                      <p>
                        <span className="font-semibold">In treno:</span>{" "}
                        raggiungi Trento con i treni Trenitalia o Italo, quindi
                        prendi la ferrovia Trento–Malé–Mezzana e scendi alla
                        stazione di Daolasa–Commezzadura, a pochi minuti
                        dall&apos;appartamento.
                      </p>
                      <p>
                        <span className="font-semibold">In aereo:</span> gli
                        aeroporti più vicini sono Verona, Bergamo e Milano. Da
                        lì puoi proseguire in auto a noleggio o in treno verso
                        Trento e poi Daolasa–Commezzadura.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        Rosa Blu Apartment is located in Via del Zicol 4, 38020
                        Commezzadura (TN), in the heart of Val di Sole.
                      </p>
                      <p>
                        <span className="font-semibold">By car:</span> take the
                        A22 Brenner motorway and exit at Trento Nord or S.
                        Michele all&apos;Adige, then follow the signs to Val di
                        Non / Val di Sole (SS43 then SS42) until Commezzadura.
                        In the village, follow Via del Zicol to number 4.
                      </p>
                      <p>
                        <span className="font-semibold">By train:</span> reach
                        Trento by Trenitalia or Italo, then take the
                        Trento–Malé–Mezzana railway and get off at
                        Daolasa–Commezzadura station, a few minutes from the
                        apartment.
                      </p>
                      <p>
                        <span className="font-semibold">By plane:</span> the
                        nearest airports are Verona, Bergamo and Milan. From
                        there you can rent a car or take a train to Trento and
                        then Daolasa–Commezzadura.
                      </p>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
